import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EnvelopeSimple, Check, X as XIcon, Warning, ArrowClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  sent: "border-[#22c55e]/40 text-[#22c55e]",
  failed: "border-[#FF2D2D] text-[#FF2D2D]",
  skipped: "border-[#404040] text-[#a3a3a3]",
  pending: "border-[#262626] text-[#a3a3a3]",
};

export default function AdminEmailDiagnostics() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/health");
      setHealth(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load email health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Enter a valid email"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email/test", { to: testTo.trim().toLowerCase() });
      setTestResult(data);
      if (data.ok) toast.success(`Sent — Resend id: ${data.resend_id}`);
      else toast.error(`${data.error_type || "Failed"}: ${data.error}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send test");
    } finally {
      setTesting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Diagnostics</div>
          <h1 className="font-display text-5xl mt-2">Email delivery</h1>
          <p className="text-[#a3a3a3] mt-2 text-sm max-w-xl">Check the Resend integration, send a test, and review the last 20 send attempts.</p>
        </div>
        <button
          onClick={load}
          data-testid="email-refresh"
          className="btn-outline px-4 py-3 text-sm uppercase tracking-wider flex items-center gap-2"
        >
          <ArrowClockwise size={14} /> Refresh
        </button>
      </div>

      {loading || !health ? (
        <div className="text-[#a3a3a3]">Loading…</div>
      ) : (
        <>
          {/* Config strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626] mb-6">
            <div className="bg-[#0a0a0a] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">API Key</div>
              <div className="font-display text-2xl mt-1 flex items-center gap-2" data-testid="health-api-key">
                {health.resend_api_key_present
                  ? <><Check size={18} weight="bold" className="text-[#22c55e]" /> <span>Set</span></>
                  : <><XIcon size={18} weight="bold" className="text-[#FF2D2D]" /> <span>Missing</span></>
                }
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">{health.resend_api_key_length} chars</div>
            </div>
            <div className="bg-[#0a0a0a] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">From address</div>
              <div className="font-mono text-sm mt-1 break-all" data-testid="health-from">{health.from_email}</div>
            </div>
            <div className="bg-[#0a0a0a] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Public base URL</div>
              <div className="font-mono text-sm mt-1 break-all" data-testid="health-base">{health.public_base_url}</div>
            </div>
            <div className="bg-[#0a0a0a] p-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3]">Send totals</div>
              <div className="font-display text-2xl mt-1 flex items-center gap-3 tabular-nums" data-testid="health-totals">
                <span className="text-[#22c55e]">{health.totals.sent}</span>
                <span className="text-[#FF2D2D] text-base">{health.totals.failed}</span>
                <span className="text-[#a3a3a3] text-base">{health.totals.skipped}</span>
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">sent · failed · skipped</div>
            </div>
          </div>

          {/* Test send */}
          <div className="border border-[#FF2D2D] bg-[#0a0a0a] p-5 mb-6" data-testid="test-send-block">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-1">Send a test email</div>
            <h2 className="font-display text-2xl mb-3">Verify deliverability</h2>
            <p className="text-xs text-[#a3a3a3] mb-4">Send to your own email — if it doesn't arrive within 1 minute, check spam, then check Resend dashboard for the message status (delivered / bounced / dropped).</p>
            <div className="flex gap-2 flex-wrap">
              <input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") sendTest(); }}
                placeholder="you@gmail.com"
                data-testid="test-to-input"
                className="flex-1 min-w-[240px] bg-[#050505] border border-[#262626] px-4 py-2.5 focus:border-[#FF2D2D] outline-none text-sm"
              />
              <button
                onClick={sendTest}
                disabled={testing}
                data-testid="test-send-btn"
                className="btn-primary px-5 py-2.5 text-sm font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
              >
                <PaperPlaneTilt size={14} weight="bold" />
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>

            {testResult && (
              <div
                data-testid="test-result"
                className={`mt-4 p-4 border ${testResult.ok ? "border-[#22c55e]/40 bg-[#22c55e]/5" : "border-[#FF2D2D] bg-[#FF2D2D]/5"}`}
              >
                {testResult.ok ? (
                  <div className="flex items-start gap-2 text-sm">
                    <Check size={16} weight="bold" className="text-[#22c55e] mt-0.5" />
                    <div>
                      <div className="font-semibold text-[#22c55e]">Resend accepted the send</div>
                      <div className="text-xs text-[#a3a3a3] mt-1">Message id: <code className="text-white">{testResult.resend_id}</code></div>
                      <div className="text-xs text-[#a3a3a3] mt-2">⚠️ This only confirms the API call succeeded. Check the recipient's inbox (and spam folder) — and the Resend dashboard for actual delivery status.</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-sm">
                    <Warning size={16} weight="fill" className="text-[#FF2D2D] mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-semibold text-[#FF2D2D]">{testResult.error_type || "Failed"}</div>
                      <div className="text-xs text-[#a3a3a3] mt-1 break-words">{testResult.error}</div>
                      {testResult.hint && <div className="text-xs text-[#a3a3a3] mt-2 italic">{testResult.hint}</div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent log */}
          <div className="border border-[#262626]">
            <div className="bg-[#0f0f0f] px-4 py-3 text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Last 20 attempts</div>
            <div className="divide-y divide-[#262626]" data-testid="email-log">
              {health.recent.length === 0 ? (
                <div className="bg-[#0a0a0a] p-8 text-center text-[#a3a3a3] text-sm">No email send attempts logged yet.</div>
              ) : (
                health.recent.map((r) => (
                  <div key={r.id} className="bg-[#0a0a0a] p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`email-log-${r.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{r.subject}</div>
                      <div className="text-xs text-[#a3a3a3] mt-0.5 truncate">to: {r.to}</div>
                      <div className="text-xs text-[#666] mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                      {r.error && (
                        <div className="text-xs text-[#FF2D2D] mt-1 break-words">{r.error}</div>
                      )}
                      {r.resend_id && (
                        <div className="text-[10px] text-[#666] mt-0.5 font-mono break-all">id: {r.resend_id}</div>
                      )}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border shrink-0 ${STATUS_COLORS[r.status] || STATUS_COLORS.pending}`}>{r.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Troubleshooting tips */}
          <details className="mt-8 border border-[#262626] bg-[#0a0a0a] p-5">
            <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
              <EnvelopeSimple size={16} className="text-[#FF2D2D]" weight="fill" /> Troubleshooting checklist
            </summary>
            <ol className="text-xs text-[#a3a3a3] mt-4 space-y-2 list-decimal list-inside">
              <li>If <strong className="text-white">API Key</strong> shows Missing → set <code className="text-white">RESEND_API_KEY</code> in your environment and redeploy.</li>
              <li>If your test email succeeds but recipients don't get production emails → check the Resend domain dashboard at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#FF2D2D] underline">resend.com/domains</a>. The <strong className="text-white">{health.from_email}</strong> domain must be fully verified (SPF + DKIM + DMARC all green).</li>
              <li>If a domain is unverified, recipients' mail servers (Gmail, Outlook) silently drop or send to spam — Resend still returns success.</li>
              <li>Check <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="text-[#FF2D2D] underline">resend.com/emails</a> for per-message delivery status (delivered/bounced/dropped).</li>
              <li>For production, ensure these env vars are set in your deploy: <code className="text-white">RESEND_API_KEY</code>, <code className="text-white">RESEND_FROM_EMAIL</code>, <code className="text-white">PUBLIC_BASE_URL=https://vzngfx.com</code>.</li>
            </ol>
          </details>
        </>
      )}
    </AdminLayout>
  );
}
